import { useAppSelector, useAppDispatch } from '../../../../store/redux/hooks';
import {
  addFormField,
  removeFormField,
} from '../../../../store/redux/slices/form.slice';
import { clearFormFieldErrors } from '../../../../store/redux/slices/templateErrors.slice';
import SectionWrapper from '../SectionWrapper';
import { Box, Flex } from '@chakra-ui/react';
import FormField from './FormField';
import { FormFieldState } from '../../../../store/types/form';
import AddMoreButton from '../AddMoreButton';
import { useEffect, useMemo, useRef, useState } from 'react';

const FormFields = () => {
  const formFields = useAppSelector((s) => s.form.formFields);
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const templateFields = useAppSelector((s) => s.template.fields);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const justAddedRef = useRef(false);
  const dispatch = useAppDispatch();

  const availableTemplateFieldIds = useMemo(
    () =>
      new Set(
        Object.values(templateFields).flatMap((section) =>
          (Object.values(section ?? {}) as Array<{ id?: string } | null>)
            .filter((field) => field != null && field.id)
            .map((field) => (field as { id: string }).id)
        )
      ),
    [templateFields]
  );

  useEffect(() => {
    formFields.forEach((f, index) => {
      if (f.id && f.id !== 'none' && !availableTemplateFieldIds.has(f.id)) {
        dispatch(clearFormFieldErrors(index));
        dispatch(removeFormField(f._clientId ?? f.id));
      }
    });
  }, [availableTemplateFieldIds, formFields, dispatch]);

  useEffect(() => {
    const connectedFieldsCount = formFields.filter(
      (f) => f.id && f.id !== 'none' && availableTemplateFieldIds.has(f.id)
    ).length;
    setIsLimitReached(connectedFieldsCount >= availableTemplateFieldIds.size);
  }, [formFields, availableTemplateFieldIds]);

  useEffect(() => {
    if (justAddedRef.current) {
      justAddedRef.current = false;
    }
  }, [formFields]);

  return (
    <SectionWrapper
      title="Form fields"
      openOnLoad={true}
      id="form-fields-section"
      subtitle="Add the fields a user will fill in before receiving their pass."
      tooltip="Each form field will appear on your custom form. Use this section to define its type, label, and connection to the pass."
    >
      <Flex direction="column" gap={2} mb={2}>
        {formFields.map((field: FormFieldState, index: number) => (
          <FormField
            key={field._clientId ?? `field-${index}`}
            field={field}
            index={index}
            isDisabled={!formEnabled}
            isTypeLockedToDropdown={isLimitReached && field.id === 'none'}
            defaultOpen={
              index === formFields.length - 1 && justAddedRef.current
            }
          />
        ))}
      </Flex>
      <Box>
        <AddMoreButton
          onClick={() => {
            justAddedRef.current = true;
            dispatch(
              addFormField(
                isLimitReached ? { asGroupTagOnly: true } : undefined
              )
            );
          }}
          text={`Add ${formFields.length > 0 ? 'another field' : 'a field'}`}
          isDisabled={!formEnabled}
        />
      </Box>
    </SectionWrapper>
  );
};

export default FormFields;
